import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { CookieConsent, getConsentPreferences, saveConsentPreferences } from './CookieBanner';
import { Link } from 'react-router-dom';

interface CookiePreferencesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CookiePreferencesDialog({ open, onOpenChange }: CookiePreferencesDialogProps) {
  const [preferences, setPreferences] = useState<CookieConsent>(getConsentPreferences());

  useEffect(() => {
    if (open) {
      setPreferences(getConsentPreferences());
    }
  }, [open]);

  const handleSave = () => {
    const updatedPreferences = {
      ...preferences,
      timestamp: Date.now(),
    };
    saveConsentPreferences(updatedPreferences);
    onOpenChange(false);
  };

  const handleAcceptAll = () => {
    const allAccepted: CookieConsent = {
      necessary: true,
      functional: true,
      administrative: true,
      timestamp: Date.now(),
    };
    saveConsentPreferences(allAccepted);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Cookie Preferences</DialogTitle>
          <DialogDescription>
            Manage your cookie preferences. You can enable or disable different types of cookies below.{' '}
            <Link to="/cookie-policy" className="text-primary hover:underline">
              View our full Cookie Policy
            </Link>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Strictly Necessary */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-1">
                <Label className="text-base font-semibold">Strictly Necessary Cookies</Label>
                <p className="text-sm text-muted-foreground">
                  These cookies are essential for the website to function properly. They enable core functionality 
                  such as security, authentication, and basic features. The website cannot function properly without 
                  these cookies.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  <strong>Used for:</strong> Supabase authentication tokens, session management
                </p>
              </div>
              <Switch
                checked={preferences.necessary}
                disabled
                className="mt-1"
              />
            </div>
          </div>

          <Separator />

          {/* Functional */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-1">
                <Label className="text-base font-semibold">Functional Cookies</Label>
                <p className="text-sm text-muted-foreground">
                  These cookies enable enhanced functionality and personalisation, such as remembering your 
                  preferences and settings. If you do not allow these cookies, some or all of these services 
                  may not function properly.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  <strong>Used for:</strong> Sidebar state preferences, dismissed announcement tracking
                </p>
              </div>
              <Switch
                checked={preferences.functional}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, functional: checked })
                }
                className="mt-1"
              />
            </div>
          </div>

          <Separator />

          {/* Administrative */}
          <div className="space-y-3">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-1">
                <Label className="text-base font-semibold">Administrative Cookies</Label>
                <p className="text-sm text-muted-foreground">
                  These cookies are used by administrators to manage and support users. They enable features 
                  such as user impersonation for support purposes. Only relevant for admin users.
                </p>
                <p className="text-xs text-muted-foreground mt-2">
                  <strong>Used for:</strong> Admin impersonation state
                </p>
              </div>
              <Switch
                checked={preferences.administrative}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, administrative: checked })
                }
                className="mt-1"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="secondary" onClick={handleAcceptAll}>
            Accept All
          </Button>
          <Button onClick={handleSave}>
            Save Preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
